import * as Sentry from '@sentry/react';
import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { propTypesLocalization } from '../../../constants/Proptypes';
import { propTypesDataEventCards } from './EventCards.proptypes';

import Button from '../../../../ui/Atoms/Button';
import EventCard from '../../../../ui/Molecules/EventCard';
import ThreeEventCards from '../../../../ui/Organisms/ThreeEventCards';

import { useSize } from '../../../hooks/useSize';
import { getStaticUrl } from '../../../utils/getStaticUrl';
import { addTrackingParamsToUrl } from '../../../utils/trackingParams';

import { DocumentType } from '../../../constants/Enums';
import * as S from './EventCards.styles';

const getRenderedList = cardList => {
  const returnedCards = cardList?.map((card, index) => {
    const { ...props } = card;

    return <EventCard key={`${card}-${index}`} {...props} />;
  });

  return returnedCards;
};

export const EventCardsSlice = ({ slice, localization, documentType }) => {
  if (!slice) {
    return null;
  }

  const isMobile = useSize().width <= 768;

  const data = slice.primary || {};
  const items = slice.items || {};

  if (items?.length < 1) {
    Sentry.captureMessage('Error: missing required data in EventCards slice');
    return null;
  }

  const { data: localizationData } = localization || {};
  const {
    use_event_template_label: actionLabel,
    see_event_template_label: watchLabel,
    event_author_prefix: authorPrefix,
    default_template_author: { document: defaultAuthorDocument } = {},
    question_number: question,
    questions_number: questions,
  } = localizationData || '';

  const {
    data: {
      first_name: defaultAuthorFirstName,
      last_name: defaultAuthorLastName,
    } = {},
  } = defaultAuthorDocument || {};

  const defaultAuthorFullName = `${defaultAuthorFirstName}${
    defaultAuthorLastName ? ` ${defaultAuthorLastName}` : ''
  }`;

  const {
    event_cards_title: title,
    is_title_centered: isTitleCentered,
    event_cards_cta_label: ctaLabel,
    event_cards_cta_link: { url: ctaUrl, target } = {},
  } = data || '';

  const generateCardList = items => {
    const cardList = [];

    items?.map(item => {
      const { event_cards_page: { document: itemDocument } = {} } = item || {};
      const { url, data: itemData } = itemDocument || {};
      const {
        description,
        template_author: { document: templateAuthorDocument } = {},
        event_slug: eventSlug,
        illustration: { alt: pictureAlt, localFile: localPicture } = {},
        number_of_questions: numberOfQuestion,
        title: eventTitle,
      } = itemData || '';

      const pictureProps = {
        imageObject: getImage(localPicture),
        imageUrl: getStaticUrl(localPicture?.publicURL),
        altText: pictureAlt,
        radius: '15px',
        width: '100%',
      };

      const {
        data: {
          company: authorCompany,
          first_name: authorFirstName,
          last_name: authorLastName,
          job_title: authorJob,
          profile_picture: {
            localFile: localAuthorProfilePicture,
            alt: authorProfilePictureAlt,
          } = '',
        } = {},
      } = templateAuthorDocument || {};

      const authorFullName = `${authorFirstName}${
        authorLastName ? ` ${authorLastName}` : ''
      }`;

      const authorProfilePictureProps = {
        imageUrl: getStaticUrl(localAuthorProfilePicture?.publicURL),
        altText: authorProfilePictureAlt,
      };

      if (
        !pictureProps?.imageUrl ||
        !eventTitle ||
        !description ||
        !eventSlug ||
        !numberOfQuestion
      ) {
        return null;
      }

      return cardList.push({
        actionLabel,
        watchLabel,
        author: authorFullName || defaultAuthorFullName,
        authorJob,
        authorCompany,
        authorProfilePictureProps,
        authorPrefix,
        description,
        eventSlug,
        numberOfQuestion,
        pictureProps,
        question,
        questions,
        title: eventTitle,
        url: addTrackingParamsToUrl(url),
      });
    });

    return cardList;
  };

  const [cardAndLink, setCardAndLink] = useState({
    generatedCardList: generateCardList(items),
    link: ctaUrl,
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCardAndLink({
        generatedCardList: generateCardList(items),
        link: addTrackingParamsToUrl(ctaUrl),
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setCardAndLink({
        generatedCardList: generateCardList(items),
        link: addTrackingParamsToUrl(ctaUrl),
      });
    };

    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  const returnCards =
    documentType === DocumentType.POST
      ? getRenderedList(cardAndLink?.generatedCardList).slice(0, 1)
      : getRenderedList(cardAndLink?.generatedCardList);

  const isPostSlice = documentType === DocumentType.POST;

  return (
    <S.Wrapper isPostSlice={isPostSlice}>
      {items.length > 1 || isPostSlice ? (
        <ThreeEventCards
          documentType={documentType}
          isSlice={true}
          isTitleCentered={isTitleCentered}
          title={title}
        >
          {returnCards}
        </ThreeEventCards>
      ) : (
        <S.SingleCardWrapper>
          {title && <S.StyledTitle>{title}</S.StyledTitle>}
          <S.TransformContainer>
            <EventCard
              actionLabel={actionLabel}
              watchLabel={watchLabel}
              author={cardAndLink?.generatedCardList[0]?.author}
              authorPrefix={authorPrefix}
              authorJob={cardAndLink?.generatedCardList[0]?.authorJob}
              authorProfilePictureProps={
                cardAndLink?.generatedCardList[0]?.authorProfilePictureProps
              }
              authorCompany={cardAndLink?.generatedCardList[0]?.authorCompany}
              description={cardAndLink?.generatedCardList[0]?.description}
              eventSlug={cardAndLink?.generatedCardList[0]?.eventSlug}
              isHighlighted={!isMobile && !isPostSlice}
              numberOfQuestion={
                cardAndLink?.generatedCardList[0]?.numberOfQuestion
              }
              pictureProps={cardAndLink?.generatedCardList[0]?.pictureProps}
              question={question}
              questions={questions}
              title={cardAndLink?.generatedCardList[0]?.title}
              url={cardAndLink?.generatedCardList[0]?.url}
            />
          </S.TransformContainer>
        </S.SingleCardWrapper>
      )}
      {!isPostSlice && (
        <S.ButtonWrapper isHigher={items.length === 1}>
          {ctaUrl && ctaLabel && (
            <Button link={cardAndLink?.link} target={target}>
              {ctaLabel}
            </Button>
          )}
        </S.ButtonWrapper>
      )}
    </S.Wrapper>
  );
};

EventCardsSlice.propTypes = {
  /**
   * Optionnal document type in case the slice
   * comes from a specific document and needs a specific
   * design.
   */
  documentType: PropTypes.string,
  /**
   * Localization document.
   */
  localization: PropTypes.shape(propTypesLocalization).isRequired,
  /**
   * Content of the EventCards slice.
   */
  slice: PropTypes.shape(propTypesDataEventCards).isRequired,
};
